import type { ReactNode } from "react";
import {
  UH_MONTHS,
  nextAvailableUnitHydName,
  type UhMonth,
  type UnitHydMonth,
  type UnitHydResponse,
  type UnitHydrograph,
} from "../model/inp/rdii.ts";
import type { TextKey } from "../i18n/en.ts";
import { useT } from "../i18n/hooks.ts";
import { TrashIcon } from "./TrashIcon.tsx";
import { PlusIcon } from "./PlusIcon.tsx";
import { IconButton } from "./IconButton.tsx";
import { NumberField } from "./fields.tsx";
import { NamedCollectionList } from "./NamedCollectionList.tsx";
import { NamedCollectionHeader } from "./NamedCollectionHeader.tsx";
import { useNamedCollection } from "./useNamedCollection.ts";

const RESPONSE_DEFAULTS: UnitHydResponse = { r: 0, t: 0, k: 0, iaMax: 0, iaRecov: 0, iaInit: 0 };

const MONTH_LABEL_KEYS: Record<UhMonth, TextKey> = {
  ALL: "rdii.monthAll",
  JAN: "climate.jan",
  FEB: "climate.feb",
  MAR: "climate.mar",
  APR: "climate.apr",
  MAY: "climate.may",
  JUN: "climate.jun",
  JUL: "climate.jul",
  AUG: "climate.aug",
  SEP: "climate.sep",
  OCT: "climate.oct",
  NOV: "climate.nov",
  DEC: "climate.dec",
};

export function UnitHydrographsEditor({
  hydrographs,
  onChange,
  selectedName,
  onSelect,
  raingageNames,
}: {
  hydrographs: UnitHydrograph[];
  onChange: (hydrographs: UnitHydrograph[]) => void;
  selectedName: string | null;
  onSelect: (name: string | null) => void;
  raingageNames: string[];
}) {
  const t = useT();
  const {
    selected,
    add,
    remove: deleteHydrograph,
    rename: renameSelected,
    update,
  } = useNamedCollection(hydrographs, onChange, selectedName, onSelect);

  function addHydrograph() {
    const name = nextAvailableUnitHydName(new Set(hydrographs.map((h) => h.name)));
    add({ name, rainGage: raingageNames[0] ?? "", months: [] });
  }

  function patch(fields: Partial<UnitHydrograph>) {
    update((h) => ({ ...h, ...fields }));
  }

  function patchMonth(month: UhMonth, fields: Partial<UnitHydMonth>) {
    if (!selected) return;
    patch({
      months: selected.months.map((m) => (m.month === month ? { ...m, ...fields } : m)),
    });
  }

  const usedMonths = new Set(selected?.months.map((m) => m.month) ?? []);
  const availableMonths = UH_MONTHS.filter((m) => !usedMonths.has(m));

  function addMonth() {
    if (!selected || availableMonths.length === 0) return;
    patch({
      months: [
        ...selected.months,
        { month: availableMonths[0], short: null, medium: null, long: null },
      ],
    });
  }

  function removeMonth(month: UhMonth) {
    if (!selected) return;
    patch({ months: selected.months.filter((m) => m.month !== month) });
  }

  return (
    <div className="named-collection-editor">
      <NamedCollectionList
        items={hydrographs}
        selectedName={selectedName}
        onSelect={onSelect}
        onAdd={addHydrograph}
        addLabel={t("rdii.addHydrograph")}
      />
      <div className="named-collection-detail">
        {!selected && <p className="property-panel-hint">{t("rdii.noHydrographSelected")}</p>}
        {selected && (
          <>
            <NamedCollectionHeader
              name={selected.name}
              onRename={renameSelected}
              onDelete={() => deleteHydrograph(selected.name)}
              deleteLabel={t("rdii.deleteHydrograph")}
            >
              <label className="field-pill">
                <span className="field-pill-label">{t("rdii.rainGage")}</span>
                <select
                  value={selected.rainGage}
                  onChange={(e) => patch({ rainGage: e.target.value })}
                >
                  <option value="" />
                  {raingageNames.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
              </label>
            </NamedCollectionHeader>

            {selected.months.map((month) => (
              <section key={month.month} className="property-panel-group">
                <div className="named-collection-detail-header">
                  <h4 className="named-collection-subsection-title">
                    {t(MONTH_LABEL_KEYS[month.month])}
                  </h4>
                  <IconButton
                    icon={<TrashIcon />}
                    label={t("rdii.deleteMonth")}
                    onClick={() => removeMonth(month.month)}
                    danger
                  />
                </div>
                <ResponseSection
                  title={t("rdii.responseShort")}
                  response={month.short}
                  onChange={(r) => patchMonth(month.month, { short: r })}
                />
                <ResponseSection
                  title={t("rdii.responseMedium")}
                  response={month.medium}
                  onChange={(r) => patchMonth(month.month, { medium: r })}
                />
                <ResponseSection
                  title={t("rdii.responseLong")}
                  response={month.long}
                  onChange={(r) => patchMonth(month.month, { long: r })}
                />
              </section>
            ))}
            <button className="button" onClick={addMonth} disabled={availableMonths.length === 0}>
              <PlusIcon />
              {t("rdii.addMonth")}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function ResponseSection({
  title,
  response,
  onChange,
}: {
  title: string;
  response: UnitHydResponse | null;
  onChange: (response: UnitHydResponse | null) => void;
}) {
  const t = useT();
  return (
    <LayerSection
      title={title}
      enabled={response !== null}
      onToggle={(on) => onChange(on ? { ...RESPONSE_DEFAULTS } : null)}
    >
      {response && (
        <div className="named-collection-fields">
          <NumberField
            label={t("rdii.r")}
            value={response.r}
            onChange={(v) => onChange({ ...response, r: v })}
          />
          <NumberField
            label={t("rdii.t")}
            value={response.t}
            onChange={(v) => onChange({ ...response, t: v })}
          />
          <NumberField
            label={t("rdii.k")}
            value={response.k}
            onChange={(v) => onChange({ ...response, k: v })}
          />
          <NumberField
            label={t("rdii.iaMax")}
            value={response.iaMax}
            onChange={(v) => onChange({ ...response, iaMax: v })}
          />
          <NumberField
            label={t("rdii.iaRecov")}
            value={response.iaRecov}
            onChange={(v) => onChange({ ...response, iaRecov: v })}
          />
          <NumberField
            label={t("rdii.iaInit")}
            value={response.iaInit}
            onChange={(v) => onChange({ ...response, iaInit: v })}
          />
        </div>
      )}
    </LayerSection>
  );
}

function LayerSection({
  title,
  enabled,
  onToggle,
  children,
}: {
  title: string;
  enabled: boolean;
  onToggle: (enabled: boolean) => void;
  children: ReactNode;
}) {
  return (
    <div>
      <h5 className="named-collection-subsection-title lid-layer-toggle">
        <label>
          <input type="checkbox" checked={enabled} onChange={(e) => onToggle(e.target.checked)} />
          {title}
        </label>
      </h5>
      {children}
    </div>
  );
}
